// frontend/app/components/lessonPlans/LessonSummarySurface.js
//
// Where an asset's authored summary lands in the ORDINARY lesson — the one a
// teacher plans in and a student works through, as opposed to the projector.
//
// Present mode has had this since 2026-08-27 (LessonPresentMode.showSummary →
// PresentSarasPanel). The workspace has not, because AITutorApp never passed
// `onShowSummary` down, and LessonBlockRenderer draws no control without it —
// so 179 authored descriptions across 37 lessons have been invisible to every
// teacher and student who was not presenting. This is the surface that half of
// the wiring was missing.
//
// THE EXCHANGE IS CANNED, AND CANNOT BE ANYTHING ELSE.
// This component takes a label and a string. It takes no ask callback, no
// token, no fetch and no client; it imports nothing that can open a socket.
// The "no network call" property therefore holds by construction rather than by
// anyone remembering — there is no request here to forget to cancel, no
// response to discard, and nothing a future edit could accidentally await.
// It is the same guarantee present mode gets from routing summaries around
// `askQuestionFromSlide` instead of through it.
//
// AND IT IS CLOSED: NO FOLLOW-UP.
// Saras did not write these words and has not seen them. "Explain that again"
// would reach a model that does not know what "that" is, in front of a class
// that has just read it — or, worse, would silently start a real conversation
// the teacher would then read as a continuation of one that never happened. So
// there is no composer on this panel at all. Not a disabled one: nothing to
// type into, so nothing to type into and wonder why it did nothing.
//
// That is also why this REPLACES the Saras rail rather than dropping a turn
// into it. A fabricated question-and-answer pushed into the rail's history
// would sit directly above the rail's own input box, and the very next thing a
// teacher typed would go to a conversation that has never contained the
// summary. Un-rendering the rail for the moment the summary is up is what makes
// "no follow-up" a fact about the component tree instead of a note in a file.
//
// ONE RENDERING, TWO CONTAINERS — see the note on `railVisible` below.

import React from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { ActionButton } from "./blocks/LessonBlockShell";
import { getLessonColors } from "./lessonPlanTheme";

// The Saras rail's own user-bubble blue, quoted rather than tokenised. This one
// hex is what makes the exchange read as the rail's exchange, and it is not a
// lesson token because there is no lesson token for "the thing the user said" —
// the closest, `accentPrimary`, is Saras blue, which in dark mode (#38bdf8)
// cannot carry white text. This value can, in both themes.
const USER_BUBBLE = "#2563eb";

/**
 * The summary itself: the control's label as the question, the author's words
 * as the answer, and one way out.
 *
 * Drawn once and mounted in whichever container the viewport has (see
 * LessonSummarySurface). Two renderings of the same words would be the
 * lookalike that drifts — the day the answer bubble changes, the copy nobody is
 * looking at is the one that ends up wrong.
 */
export const LessonSummaryPanel = ({
  label,
  text,
  colors,
  // What pressing the way out actually gets you, which differs by viewport and
  // so must not be described by one fixed string. See the surface below.
  dismissLabel,
  onDismiss,
  t,
}) => {
  const translate = t || ((value) => value);

  return (
    <View
      testID="lesson-summary-panel"
      style={[
        styles.panel,
        { backgroundColor: colors.railBg, borderColor: colors.border },
      ]}
    >
      {/* The rail's identity, because this is standing in for the rail. A
          teacher who pressed a Saras-marked control has to arrive somewhere
          that is recognisably Saras. */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <MaterialCommunityIcons
          name="robot-happy-outline"
          size={20}
          color={colors.accentPrimary}
        />
        <Text style={[styles.headerText, { color: colors.textPrimary }]}>
          {translate("Saras AI")}
        </Text>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* The question, right-aligned and solid, exactly as the rail draws
            what the user said. The teacher did say it: they pressed it. */}
        {label ? (
          <View style={styles.questionRow}>
            <View style={[styles.questionBubble, { backgroundColor: USER_BUBBLE }]}>
              <Text style={styles.questionText}>{translate(label)}</Text>
            </View>
          </View>
        ) : null}

        <Text style={[styles.answerCaption, { color: colors.textMuted }]}>
          {translate("Saras AI")}
        </Text>
        <View
          style={[
            styles.answerBubble,
            { backgroundColor: colors.subtleBg, borderColor: colors.border },
          ]}
        >
          {/* Plain text, not markdown. All 179 authored summaries are prose,
              and present mode draws its answers the same way. */}
          <Text style={[styles.answerText, { color: colors.textPrimary }]}>
            {text}
          </Text>
        </View>
      </ScrollView>

      {/* The only control, and the only way out. It is an ActionButton like
          every other lesson control rather than a rail-shaped one, because
          what it does is a lesson action: it puts this away. */}
      <View style={[styles.footer, { borderTopColor: colors.border }]}>
        <ActionButton
          colors={colors}
          label={dismissLabel}
          icon="arrow-left"
          variant="outline"
          onPress={onDismiss}
        />
      </View>
    </View>
  );
};

/**
 * The summary in the lesson, in whichever container this viewport has.
 *
 * `summary` is what assetSummaryFor produced, narrowed by the caller to the
 * authored case. An empty one renders nothing — a `prompt`-kind summary has no
 * text and went down the ask path instead, so it falls out here for free.
 *
 * WHY THE NARROW CASE IS AN OVERLAY, AND WHY THERE IS ONE AT ALL.
 * The Saras rail only exists at ≥1280px (AITutorApp `lessonRailVisible`). The
 * easy answer — draw the control only where the rail is — would leave the
 * summaries invisible on every laptop and tablet, which is the bug this work
 * exists to fix, just moved. The other easy answer, drawing the control
 * everywhere and letting it open a rail the viewport does not have, is worse:
 * a button that does nothing teaches a teacher to stop trusting the buttons.
 *
 * So the control is always live and the panel goes where there is room: into
 * the rail's column when there is one, and over the lesson when there is not —
 * the same resolution present mode reached, where the panel covers the stage
 * because a projector has no rail either. It is the SAME panel in both, so
 * there is still exactly one rendering of the author's words.
 *
 * The way out is labelled for what it actually does, per viewport. With a rail,
 * dismissing lands you in it, where you can ask — "Ask Saras a question", the
 * present-mode wording. Without one, dismissing lands you back in the lesson
 * and there is nowhere to ask, so promising a question would be the same empty
 * promise in words instead of pixels.
 */
const LessonSummarySurface = ({
  summary,
  isDarkMode = false,
  // True when the caller is showing the Saras rail, i.e. the panel has a column
  // to sit in. False means it has to cover the lesson instead.
  railVisible = false,
  railWidth = 380,
  onDismiss,
  t,
}) => {
  const translate = t || ((value) => value);
  const text = String(summary?.text || "").trim();
  if (!text) return null;

  const colors = getLessonColors(isDarkMode);
  const label = String(summary?.label || "").trim() || null;
  const panel = (
    <LessonSummaryPanel
      label={label}
      text={text}
      colors={colors}
      dismissLabel={
        railVisible
          ? translate("Ask Saras a question")
          : translate("Back to the lesson")
      }
      onDismiss={onDismiss}
      t={t}
    />
  );

  if (railVisible) {
    // In flow, in the rail's own column and at the rail's own width, so the
    // lesson beside it does not reflow when the summary opens and closes.
    return (
      <View
        style={{
          width: railWidth,
          minWidth: railWidth,
          flexShrink: 0,
          minHeight: 0,
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        {panel}
      </View>
    );
  }

  return (
    <View style={styles.overlay} testID="lesson-summary-overlay">
      {/* Tapping away is what a teacher's hand does on a tablet. It is not the
          documented way out — the button is — so it carries its own label
          rather than competing for that one. */}
      <TouchableOpacity
        style={StyleSheet.absoluteFill}
        activeOpacity={1}
        accessibilityRole="button"
        accessibilityLabel={translate("Close summary")}
        onPress={onDismiss}
      />
      <View style={styles.overlayCard}>{panel}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    minHeight: 0,
    borderRadius: 16,
    borderWidth: 1,
    overflow: "hidden",
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerText: { fontSize: 15, fontWeight: "700", marginLeft: 8 },

  scroll: { flex: 1 },
  scrollContent: { padding: 14 },

  questionRow: { alignItems: "flex-end", marginBottom: 12 },
  questionBubble: {
    maxWidth: "90%",
    borderRadius: 12,
    borderBottomRightRadius: 2,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  questionText: { fontSize: 14, fontWeight: "600", lineHeight: 20, color: "#ffffff" },

  answerCaption: { fontSize: 12, marginBottom: 4, marginLeft: 4 },
  answerBubble: {
    borderRadius: 12,
    borderBottomLeftRadius: 2,
    borderWidth: 1,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  answerText: { fontSize: 14, lineHeight: 22 },

  footer: {
    flexDirection: "row",
    borderTopWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },

  // Over the lesson, the way present mode's panel goes over the stage. Padded
  // rather than edge to edge so it reads as a panel on the lesson and not as a
  // new screen the teacher has navigated to.
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    backgroundColor: "rgba(15, 23, 42, 0.45)",
  },
  overlayCard: { width: "100%", maxWidth: 560, maxHeight: "100%", minHeight: 0 },
});

export default LessonSummarySurface;
